export type VMStatus =
  | "running"
  | "stopped"
  | "paused"
  | "suspended"
  | "migrating"
  | "error"
  | "provisioning";

export type ServiceTier = "A" | "B" | "C";

export type ReplicaStatus =
  | "unsatisfied"
  | "pending"
  | "syncing"
  | "up_to_date"
  | "stale"
  | "failed";

export interface VMReplica {
  id: number;
  vm_id: number;
  replica_node: string;
  replica_vmid: number;
  replication_mode: string;
  replication_schedule: string;
  proxmox_job_id: string | null;
  status: ReplicaStatus;
  last_sync_at: string | null;
  last_error: string | null;
  created_at: string;
  updated_at: string;
}

export interface VM {
  id: number;
  vmid: number;
  name: string;
  tenant_id: number;
  node: string;
  service_tier: string;
  cores: number;
  memory_mb: number;
  disk_gb: number;
  network_speed: string;
  ip_address: string | null;
  ci_user: string | null;
  ci_password: string | null;
  status: VMStatus;
  omer_enabled: boolean;
  replica: VMReplica | null;
  created_at: string;
  deleted_at: string | null;
  is_template?: boolean;
}

export interface Template {
  id: string;
  name: string;
  os: string;
  description: string;
  min_cores: number;
  min_memory_mb: number;
  min_disk_gb: number;
}

export type BackupPlanType = "none" | "snapshot" | "full" | "incremental";

export type NetworkSpeed = "100m" | "500m" | "1g";

export interface VMCreatePayload {
  name: string;
  service_tier: ServiceTier;
  template: string;
  cores: number;
  memory_mb: number;
  disk_gb: number;
  network_speed?: NetworkSpeed;
  preferred_node?: string;
}

export interface ServiceTierInfo {
  tier: ServiceTier;
  name: string;
  description: string;
  ha_level: string;
  replication: string;
  backup_retention_days: number;
  sla: string;
}

export const SERVICE_TIERS: ServiceTierInfo[] = [
  {
    tier: "A",
    name: "Standalone",
    description: "Single VM, no redundancy — development and testing",
    ha_level: "No HA",
    replication: "No replication",
    backup_retention_days: 7,
    sla: "99%",
  },
  {
    tier: "B",
    name: "Active / Passive",
    description: "Business applications with manual failover",
    ha_level: "HA with manual failover",
    replication: "Asynchronous replication",
    backup_retention_days: 90,
    sla: "99.9%",
  },
  {
    tier: "C",
    name: "Active / Active",
    description: "Mission-critical workloads with full high availability",
    ha_level: "Full HA with automatic failover",
    replication: "Synchronous replication across nodes",
    backup_retention_days: 365,
    sla: "99.99%",
  },
];

export interface VMConsoleInfo {
  ticket: string;
  vm_name: string;
  vnc_password: string;
  port: number;
  ci_user: string;
  ci_password: string;
  type: "websocket";
}

export interface CapacityInfo {
  node: string;
  total_cpu: number;
  used_cpu: number;
  total_ram_mb: number;
  used_ram_mb: number;
  total_disk_gb: number;
  used_disk_gb: number;
  vm_count: number;
  status: "online" | "offline" | "maintenance";
}
